'use client';

import Header from '@/components/Header';
import WithdrawalOnlyHeader from '@/components/WithdrawalOnlyHeader';
import Footer from '@/components/Footer';
import { useEffect, useRef } from 'react';
import { useConfig } from '@/app/contexts/ConfigContext';
import { LanguageOptions } from '@/types';
import FaviconSetter from '@/components/FaviconSetter';
import { useSearchParams } from 'next/navigation';
import {
  checkUrlCountry,
  getValidatedWithdrawalServiceParam,
  isMobileAppPortalHost,
} from '@/helpers';
import { LANGUAGE_OPTIONS, SERVICE_NAMES } from '@/helpers/enums';
import { withdrawalBranding } from '@/configs/withdrawalBranding';

export default function MainLayoutClient({
  children,
  host,
  isWithdrawalOnlyPortal,
}: Readonly<{
  children: React.ReactNode;
  host: string;
  isWithdrawalOnlyPortal: boolean;
}>) {
  const {
    config,
    setConfig,
    setLanguageAndWording,
    updateWording,
    setSelectedLanguage,
  } = useConfig();
  const searchParams = useSearchParams();
  const initialServiceNameRef = useRef(config.SERVICE_NAME);
  const serviceParam = searchParams.get('service');
  const validatedServiceName = getValidatedWithdrawalServiceParam(
    host,
    serviceParam,
  );
  const withdrawalServiceBranding = validatedServiceName
    ? withdrawalBranding[validatedServiceName]
    : null;
  const isWithdrawalStandalone = isWithdrawalOnlyPortal;
  const isMobileAppPortal =
    isMobileAppPortalHost(host) ||
    isMobileAppPortalHost(config.SERVICE_URL_LINK);

  useEffect(() => {
    const changeLang = searchParams
      .get('changelang')
      ?.toUpperCase() as LanguageOptions;
    const isChangeLangValid =
      changeLang && config.languages.includes(changeLang);

    const fetchConfig = async () => {
      const browserLang = navigator.language;
      const langCode = browserLang
        .split('-')[0]
        .toUpperCase() as LanguageOptions;
      const isBrowserLangAvailableInConfig =
        config.languages.includes(langCode);

      if (isChangeLangValid) {
        setLanguageAndWording(config.serviceType, changeLang);
      } else if (isBrowserLangAvailableInConfig) {
        setLanguageAndWording(config.serviceType, langCode);
      } else if (!isBrowserLangAvailableInConfig) {
        setLanguageAndWording(config.serviceType, config.languages[0]);
      } else {
        console.error('[Main Layout] Error using config');
      }

      if (
        checkUrlCountry(config.SERVICE_URL_LINK, LANGUAGE_OPTIONS.CY)
      ) {
        setSelectedLanguage('EN');
        updateWording(config.serviceType, 'CY');
      }
    };
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isWithdrawalOnlyPortal) {
      return;
    }

    const withdrawalServiceName =
      validatedServiceName ?? initialServiceNameRef.current;

    if (config.SERVICE_NAME !== withdrawalServiceName) {
      setConfig({
        ...config,
        SERVICE_NAME: withdrawalServiceName as keyof typeof SERVICE_NAMES,
      });
    }

    document.title = withdrawalServiceName;
  }, [config, isWithdrawalOnlyPortal, setConfig, validatedServiceName]);

  return (
    <>
      {isWithdrawalStandalone ? (
        validatedServiceName &&
        withdrawalServiceBranding && (
          <WithdrawalOnlyHeader
            {...withdrawalServiceBranding}
            serviceName={validatedServiceName}
          />
        )
      ) : (
        <Header />
      )}
      {!isWithdrawalStandalone && (
        <FaviconSetter faviconUrl={config.theme?.faviconSrc} />
      )}
      <div className={isMobileAppPortal ? 'pt-20 2xl:pt-[120px]' : undefined}>
        {children}
      </div>
      {!isWithdrawalStandalone && <Footer />}
    </>
  );
}
